import { Injectable, Inject } from '@angular/core';
import { DateTime } from 'luxon';
import { CookieService } from '@utils/cookie.service';
import { DomainConfigService } from '@domain/domain-config.service';
import { ILoggingService, LOGGING_SERVICE_TOKEN } from '@logging/logging.token';
import { PREFIXED_TIMEOUT } from '@auth-n/cookie-names';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class AbandonmentService {
  private readonly cookieName: string;
  private readonly timeoutMs: number;

  constructor(
    @Inject(LOGGING_SERVICE_TOKEN) private logger: ILoggingService,
    private cookieService: CookieService,
    private domainConfig: DomainConfigService
  ) {
    this.cookieName = `${domainConfig.environmentCookiePrefix}${PREFIXED_TIMEOUT}`;
    this.timeoutMs = environment.idleActivityConfig.timeoutMinutes * 60 * 1000;
  }

  isAbandoned(): boolean {
    const cookieData = this.cookieService.getCookieValue(this.cookieName);

    if (!cookieData) {
      this.logger.trace('Abandonment:', 'No stored data found');
      return false;
    }

    const expiryMs = parseInt(cookieData, 10);
    if (isNaN(expiryMs)) return false;

    const nowMs = DateTime.utc().toMillis();

    if (nowMs < expiryMs) return false;

    this.logger.trace('Abandonment: expiry time', expiryMs, 'now:', nowMs);
    return true;
  }

  setLastBecameActive(): void {
    if (this.isAbandoned()) {
      this.logger.trace('Abandonment: session already expired, not extending timeout');
      return;
    }

    this.writeTimeout();
  }

  /**
   * Unconditionally resets the timeout — used after sign-out to prevent redirect loops
   * when the login page re-initialises the app.
   */
  resetTimeout(): void {
    this.writeTimeout();
  }

  private writeTimeout(): void {
    const expiryMs = DateTime.utc().toMillis() + this.timeoutMs;
    this.cookieService.setDomainCookie(this.cookieName, expiryMs.toString(), this.domainConfig.cookieDomain);
  }
}
